import service from '../../service';
import type { MenuGroup, MenuItem, Order, ToastSelection } from '@Types/toast';
import Decimal from 'decimal.js';

export type ManualChargeMenuData = {
  menuGroup: MenuGroup;
  menuItem: MenuItem;
};

export type FetchManualChargeMenuParams = {
  restaurantGuid: string;
  menuGuid: string;
  menuGroupGuid: string;
  menuItemGuid: string;
};

export type BuildManualChargeOrderOptions = {
  menuGroup: MenuGroup;
  menuItem: MenuItem;
  amount: number;
  diningOptionGuid: string;
};

export type CreateOpenPriceSelectionOptions = {
  menuGroup: MenuGroup;
  menuItem: MenuItem;
  amount: number;
  quantity?: number;
};

export async function fetchManualChargeMenu({
  restaurantGuid,
  menuGuid,
  menuGroupGuid,
  menuItemGuid,
}: FetchManualChargeMenuParams): Promise<ManualChargeMenuData> {
  try {
    const menusResponse = await service.getMenus(restaurantGuid, {
      guid: menuGuid,
    });

    if (!menusResponse?.menus || menusResponse.menus.length === 0) {
      if (import.meta.env.DEV) {
        console.error(`Menu "${menuGuid}" not found`);
      }
      throw new Error('Unable to load menu configuration');
    }

    const menu = menusResponse.menus[0];

    if (!menu) {
      if (import.meta.env.DEV) {
        console.error('Failed to fetch menu details for:', menu.guid);
      }
      throw new Error('Unable to load menu configuration');
    }

    if (!menu.menuGroups || menu.menuGroups.length === 0) {
      if (import.meta.env.DEV) {
        console.error(`Menu "${menuGuid}" is missing menu groups:`, menu);
      }
      throw new Error('Menu configuration incomplete');
    }

    const menuGroup = menu.menuGroups.find(
      (group) => group.guid === menuGroupGuid,
    );

    if (!menuGroup) {
      if (import.meta.env.DEV) {
        console.error(
          `Menu group "${menuGroupGuid}" not found. Available:`,
          menu.menuGroups.map((g) => g.name),
        );
      }
      throw new Error('Menu configuration incomplete');
    }

    if (!menuGroup.menuItems || menuGroup.menuItems.length === 0) {
      if (import.meta.env.DEV) {
        console.error(`Menu group "${menuGroupGuid}" is missing items`);
      }
      throw new Error('Menu configuration incomplete');
    }

    const menuItem = menuGroup.menuItems.find(
      (item) => item.guid === menuItemGuid,
    );

    if (!menuItem) {
      if (import.meta.env.DEV) {
        console.error(
          `Menu item "${menuItemGuid}" not found. Available:`,
          menuGroup.menuItems.map((i) => i.name),
        );
      }
      throw new Error('Menu configuration incomplete');
    }

    if (!menuGroup.guid || !menuItem.guid) {
      if (import.meta.env.DEV) {
        console.error('Menu group or item missing GUID');
      }
      throw new Error('Menu configuration incomplete');
    }

    return {
      menuGroup,
      menuItem,
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes('configuration')) {
      throw error;
    }
    if (import.meta.env.DEV) {
      console.error('Unexpected error fetching manual charge menu:', error);
    }
    throw new Error('Unable to load menu configuration');
  }
}

export function createOpenPriceSelection({
  menuGroup,
  menuItem,
  amount,
  quantity = 1,
}: CreateOpenPriceSelectionOptions): ToastSelection {
  if (!menuGroup.guid) {
    throw new Error('Menu group is missing required GUID');
  }

  if (!menuItem.guid) {
    throw new Error('Menu item is missing required GUID');
  }

  return {
    itemGroup: {
      guid: menuGroup.guid,
      entityType: 'MenuGroup',
    },
    item: {
      guid: menuItem.guid,
      entityType: 'MenuItem',
    },
    quantity,
    openPriceAmount: new Decimal(amount).toNumber(),
  };
}

export function buildManualChargeOrder({
  menuGroup,
  menuItem,
  amount,
  diningOptionGuid,
}: BuildManualChargeOrderOptions): Order {
  if (!diningOptionGuid) {
    throw new Error('Dining option GUID is required');
  }

  const selection = createOpenPriceSelection({
    menuGroup,
    menuItem,
    amount,
    quantity: 1,
  });

  return {
    entityType: 'Order',
    diningOption: {
      guid: diningOptionGuid,
    },
    checks: [
      {
        entityType: 'Check',
        selections: [selection],
      },
    ],
  };
}
